import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTimes, FaArrowLeft } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import './ProjectDetails.css';

// Import all project detail images
import dailyDetail1 from '../daily-collection-1.png';
import dailyDetail2 from '../daily-collection-2.png';
import dailyDetail3 from '../daily-collection-3.png';
import dailyDetail4 from '../daily-collection-4.png';
import dailyDetail5 from '../daily-collection-5.png';
import dailyDetail6 from '../daily-collection-6.png';
import sweepDetail1 from '../sweep-1.png';
import sweepDetail2 from '../sweep-2.png';
import sweepDetail3 from '../sweep-3.png';
import sweepDetail4 from '../sweep-4.png';
import sweepDetail5 from '../sweep-5.png';
import codifyDetail1 from '../codify1.png';
import codifyDetail2 from '../codify3.png';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const projectIdNum = parseInt(projectId);

  // Project data - matching Projects.js structure
  const projects = [
    {
      id: 1,
      title: 'Daily Collection Management System',
      details: [
        { image: dailyDetail1, description: 'Dashboard view showing Mr. Rashith Abeywickrama\'s Portal with key performance indicators including completion rate, collection coverage, and portfolio summary with total customers and loans.' },
        { image: dailyDetail2, description: 'Add Customer form interface for registering new customers with contact details, NIC number, phone number, email address, and supporting references.' },
        { image: dailyDetail3, description: 'Create loan page with automated loan setup including customer selection, principal amount, interest rate, term, and automatic repayment schedule calculation.' },
        { image: dailyDetail4, description: 'Payments section featuring record payment functionality with loan search, payment summary showing must collect amount, collected amount, and arrears collection totals.' },
        { image: dailyDetail5, description: 'Holidays management interface allowing administrators to add and manage holidays that automatically affect loan payment schedules and calculations.' },
        { image: dailyDetail6, description: 'Help & Video Tutorial page providing comprehensive system documentation and YouTube video guide for learning all features of the Daily Collection system.' }
      ]
    },
    {
      id: 2,
      title: 'SPEX ID Topup Random Sweep Giveaway',
      details: [
        { image: sweepDetail1, description: 'Login modal interface with email and password authentication required to access the sweep giveaway system.' },
        { image: sweepDetail2, description: 'CSV upload and parsing interface showing successful file processing with duplicate detection and removal, displaying unique game IDs loaded into the system.' },
        { image: sweepDetail3, description: 'Game IDs display in a colorful gradient grid showing all unique participant IDs ready for the random sweep selection process.' },
        { image: sweepDetail4, description: 'Winners selection results screen displaying selected winners with confetti animation and numbered list of winning game IDs.' },
        { image: sweepDetail5, description: 'Download winners section with package name input field and download functionality to export selected winners as a text file.' }
      ]
    },
    {
      id: 4,
      title: 'Receipt Printing System',
      details: [
        { image: codifyDetail1, description: 'Create Receipt/Invoice form interface with client information and project details input fields, featuring real-time receipt preview with automatic receipt number generation.' },
        { image: codifyDetail2, description: 'Receipt preview panel with action buttons for printing, downloading PDF, sending email, and saving receipts, displaying formatted invoice with client billing and project information.' }
      ]
    }
  ];

  const project = projects.find(p => p.id === parseInt(projectId));

  useEffect(() => {
    if (!project) {
      navigate('/');
    }
  }, [project, navigate]);

  if (!project || !project.details) {
    return null;
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.details.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.details.length - 1 : prev - 1
    );
  };

  return (
    <div className="project-details-page">
      <Navbar />
      <div className="project-details-container">
        <button 
          className="back-button"
          onClick={() => {
            navigate(-1);
          }}
        >
          <FaArrowLeft /> Back to Projects
        </button>

        <div className="project-details-content">
          <h1 className="project-details-title">{project.title}</h1>
          
          <div className="details-slideshow-container">
            <div className="details-image-container">
              <motion.img
                key={currentImageIndex}
                src={project.details[currentImageIndex].image}
                alt={`${project.title} - Image ${currentImageIndex + 1}`}
                className="details-image"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              />
              
              {project.details.length > 1 && (
                <>
                  <button
                    className="details-nav-btn details-nav-prev"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="details-nav-btn details-nav-next"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}

              <div className="details-image-counter">
                {currentImageIndex + 1} / {project.details.length}
              </div>
            </div>

            <div className="details-description">
              <p>{project.details[currentImageIndex].description}</p>
            </div>

            {project.details.length > 1 && (
              <div className="details-thumbnails">
                {project.details.map((detail, index) => (
                  <motion.button
                    key={index}
                    className={`details-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <img
                      src={detail.image}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;

